/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCallWithJobSwaggers, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { OperationStatusBaseSwagger } from 'src/core/services/api/operationSwagger';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    GptJobInputSwagger,
    GptJobSwagger,
    GptPredictionDecisionSubmissionJobInputSwagger,
    GptPredictionDocument
} from 'src/customText/core/services/gpt/customTextGptSwagger';
import {
    CustomTextExportedProjectAssetsSwagger,
    CustomTextProjectKindSwagger
} from 'src/customText/core/services/projects/customTextProjectSwaggers';

export const getCustomTextGptService = createService(({ context, getEnvironment }) => {
    const { isProduction, webApiUrl } = getEnvironment({ apiName: 'customText' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const getUrlBuilder = (projectName?: string) =>
        urlBuilder()
            .baseUrl(webApiUrl)
            .resource('internal')
            .project(projectName)
            .queryParam('api-version', LuisConstants.CustomText.InternalApiVersion);

    const runGptJob = async ({
        classes,
        handler,
        extractors,
        projectName,
        documentsNames,
        azureOpenAiResourceRegion,
        azureOpenAiDeploymentName
    }: {
        classes?: string[];
        projectName: string;
        extractors?: string[];
        documentsNames: string[];
        azureOpenAiResourceRegion: string;
        azureOpenAiDeploymentName: string;
        handler: OperationHandler<GptJobSwagger>;
    }): Promise<string> => {
        const url = getUrlBuilder(projectName)
            .resource(':run-gpt')
            .build();

        const payload: GptJobInputSwagger = {
            jobType: 'ZeroShotDataLabeling',
            aoaiRegion: azureOpenAiResourceRegion,
            aoaiDeploymentName: azureOpenAiDeploymentName,
            zeroShotDataLabelingParameters: {
                documentNames: documentsNames,
                classes: classes?.map(c => ({ category: c })),
                entities: extractors?.map(e => ({ category: e }))
            }
        };

        const { headers } = await httpService.post<void>(url, payload, apiOptions);

        const jobUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];

        return (await createOperationCallWithJobSwaggers<GptJobSwagger>(jobUrl, apiOptions, handler)).data.resultUrl;
    };

    const getGptJobs = async ({ nextLink, projectName }: { nextLink?: string; projectName: string }) => {
        const url =
            nextLink ??
            getUrlBuilder(projectName)
                .resource('run-gpt', 'jobs')
                .build();

        const { data } = await httpService.get<{ jobs: GptJobSwagger[]; nextLink: string }>(url, apiOptions);

        return data;
    };

    const getGptResults = async ({
        resultUrl,
        projectName
    }: {
        resultUrl?: string;
        projectName: string;
    }): Promise<CustomTextExportedProjectAssetsSwagger> => {
        const url =
            resultUrl ??
            getUrlBuilder(projectName)
                .resource('gpt-predictions')
                .build();

        const { data: exportedProject } = await httpService.get<CustomTextExportedProjectAssetsSwagger>(url, apiOptions);

        return exportedProject;
    };

    const submitGptDecisions = async ({
        documents,
        projectName,
        projectKind
    }: {
        projectName: string;
        documents: GptPredictionDocument[];
        projectKind: CustomTextProjectKindSwagger;
    }) => {
        const url = getUrlBuilder(projectName)
            .resource(':submit-gpt-prediction-decisions')
            .build();

        const payload: GptPredictionDecisionSubmissionJobInputSwagger = {
            jobType: 'ZeroShotDataLabeling',
            submittedGptPredictions: { projectKind, documents }
        };

        const { headers } = await httpService.post<void>(url, payload, apiOptions);

        const jobUrl = (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];

        await createOperationCallWithJobSwaggers<OperationStatusBaseSwagger>(jobUrl, apiOptions, {
            timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
            retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed'
        });

        return (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];
    };

    const pollGptJob = async ({
        jobId,
        handler,
        projectName
    }: {
        jobId: string;
        projectName: string;
        handler: OperationHandler<GptJobSwagger>;
    }): Promise<string> => {
        const url = getUrlBuilder(projectName)
            .resource('run-gpt', 'jobs')
            .resource(jobId)
            .build();

        return (await createOperationCallWithJobSwaggers<GptJobSwagger>(url, apiOptions, handler)).data.resultUrl;
    };

    return { runGptJob, getGptJobs, getGptResults, submitGptDecisions, pollGptJob };
});
